import Base from '../../_config/base';

class SessionPlan extends Base {
  constructor(el) {
    super(el);
    this.init();
  }

  init() {
    this.initSessionPlan();
    this.initSessionFilter();
    this.initBookmarkFilter();
    this.initResetFilter();
    this.initFilterMenuToggle();
  }

  initSessionPlan() {
    const sessionHeaders = this.$el.querySelectorAll('.js-session-header');

    sessionHeaders.forEach((header) => {
      header.addEventListener('click', () => {
        const sessionId = header.getAttribute('data-session-id');
        const sessionDetails = this.$el.querySelector(`.js-session-details[data-session-id="${sessionId}"]`);

        header.classList.toggle('active');
        sessionDetails?.classList.toggle('active');

        // Mobile-specific behavior
        if (window.innerWidth < 768) {
          header.closest('.js-session-item')?.classList.toggle('active');
        }
      });
    });
  }

  initSessionFilter() {
    const filterOptions = this.$el.querySelectorAll('.js-filter-option');
    const sessionPlan = this.$el.querySelector('.js-session-plan');

    if (!filterOptions.length || !sessionPlan) {
      return;
    }

    // Initialize activeFilters dynamically based on available filter types
    this.activeFilters = {};
    const filterTypes = new Set();

    filterOptions.forEach((option) => {
      const { filterType } = option.dataset;
      if (filterType) {
        filterTypes.add(filterType);
      }
    });

    // Initialize each filter type with 'all' selected
    filterTypes.forEach((filterType) => {
      this.activeFilters[filterType] = ['all'];
    });

    filterOptions.forEach((option) => {
      option.addEventListener('click', (e) => {
        this.handleFilterClick(e.target);
      });
    });
  }

  initBookmarkFilter() {
    // Initialize bookmark filter state
    this.showBookmarkedOnly = false;

    // Bind bookmark filter button
    const bookmarkFilterBtn = this.$el.querySelector('.js-bookmark-filter');
    if (bookmarkFilterBtn) {
      bookmarkFilterBtn.addEventListener('click', () => this.toggleBookmarkFilter());
    }

    // Bind bookmark session buttons
    const bookmarkSessionBtns = this.$el.querySelectorAll('.js-bookmark-button');
    bookmarkSessionBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleBookmarkSession(btn);
      });
    });

    // Update bookmark states on init
    this.updateBookmarkStates();
  }

  initResetFilter() {
    const resetFilterBtn = this.$el.querySelector('.js-reset-filter');
    if (resetFilterBtn) {
      resetFilterBtn.addEventListener('click', () => this.resetAllFilters());
    }
  }

  toggleBookmarkFilter() {
    const bookmarkFilterBtn = this.$el.querySelector('.js-bookmark-filter');
    const isActive = bookmarkFilterBtn.classList.toggle('active');
    bookmarkFilterBtn.classList.toggle('inactive', !isActive);

    // Toggle bookmark filter state
    this.$el.classList.toggle('show-bookmarked-only', isActive);

    this.applyFilters();
  }

  toggleBookmarkSession(button) {
    const sessionId = button.getAttribute('data-session-id');
    const bookmarkedSessions = SessionPlan.getBookmarkedSessions();

    if (bookmarkedSessions.includes(sessionId)) {
      // Remove from bookmarks
      const updatedBookmarks = bookmarkedSessions.filter((id) => id !== sessionId);
      localStorage.setItem('bookmarkedSessions', JSON.stringify(updatedBookmarks));
      SessionPlan.updateBookmarkButton(button, false);
    } else {
      // Add to bookmarks
      bookmarkedSessions.push(sessionId);
      localStorage.setItem('bookmarkedSessions', JSON.stringify(bookmarkedSessions));
      SessionPlan.updateBookmarkButton(button, true);
    }

    // Trigger filter update to reflect bookmark changes
    this.applyFilters();
  }

  static updateBookmarkButton(button, isBookmarked) {
    if (isBookmarked) {
      button.classList.add('active');
      button.classList.remove('inactive');
    } else {
      button.classList.add('inactive');
      button.classList.remove('active');
    }
  }

  updateBookmarkStates() {
    const bookmarkedSessions = SessionPlan.getBookmarkedSessions();
    const bookmarkButtons = this.$el.querySelectorAll('.js-bookmark-button');

    bookmarkButtons.forEach((button) => {
      const sessionId = button.getAttribute('data-session-id');
      const isBookmarked = bookmarkedSessions.includes(sessionId);
      SessionPlan.updateBookmarkButton(button, isBookmarked);
    });
  }

  static getBookmarkedSessions() {
    const stored = localStorage.getItem('bookmarkedSessions');
    return stored ? JSON.parse(stored) : [];
  }

  handleFilterClick(option) {
    const { filterType, filterValue } = option.dataset;

    if (filterValue === 'all') {
      this.handleAllFilterClick(option, filterType);
    } else {
      this.handleSpecificFilterClick(option, filterType, filterValue);
    }

    this.applyFilters();
  }

  handleAllFilterClick(option, filterType) {
    // Reset all other options in this filter group
    const filterGroup = option.closest('.session-filter__category') || option.closest('.js-session-filter');
    const otherOptions = filterGroup.querySelectorAll(`.js-filter-option[data-filter-type="${filterType}"]:not([data-filter-value="all"])`);

    otherOptions.forEach((opt) => opt.classList.remove('active'));
    option.classList.add('active');
    this.activeFilters[filterType] = ['all'];
  }

  handleSpecificFilterClick(option, filterType, filterValue) {
    const allButton = this.$el.querySelector(`.js-filter-option[data-filter-type="${filterType}"][data-filter-value="all"]`);
    allButton?.classList.remove('active');

    option.classList.toggle('active');

    if (option.classList.contains('active')) {
      this.addFilterValue(filterType, filterValue);
    } else {
      this.removeFilterValue(filterType, filterValue);
    }

    this.ensureAllButtonState(filterType, allButton);
  }

  addFilterValue(filterType, filterValue) {
    this.activeFilters[filterType] = this.activeFilters[filterType].filter((value) => value !== 'all');
    if (!this.activeFilters[filterType].includes(filterValue)) {
      this.activeFilters[filterType].push(filterValue);
    }
  }

  removeFilterValue(filterType, filterValue) {
    this.activeFilters[filterType] = this.activeFilters[filterType].filter(
      (value) => value !== filterValue,
    );
  }

  ensureAllButtonState(filterType, allButton) {
    if (this.activeFilters[filterType].length === 0 && allButton) {
      allButton.classList.add('active');
      this.activeFilters[filterType] = ['all'];
    }
  }

  applyFilters() {
    const sessionPlan = this.$el.querySelector('.js-session-plan');
    if (!sessionPlan) return;

    const sessionItems = sessionPlan.querySelectorAll('.js-session-item');

    sessionItems.forEach((item) => {
      const shouldShow = this.shouldShowSession(item);
      item.classList.toggle('filtered-out', !shouldShow);
    });

    this.hideEmptyGroups();

    // Show/hide no results message
    const noResultsMessage = this.$el.querySelector('.js-no-results-message');
    if (noResultsMessage) {
      const anyVisible = Array.from(sessionItems).some(
        (item) => !item.classList.contains('filtered-out'),
      );
      noResultsMessage.style.display = anyVisible ? 'none' : '';
    }
  }

  shouldShowSession(sessionItem) {
    // Check bookmark filter first
    const showBookmarkedOnly = this.$el.classList.contains('show-bookmarked-only');
    if (showBookmarkedOnly) {
      const sessionId = sessionItem.getAttribute('data-session-id');
      const bookmarkedSessions = SessionPlan.getBookmarkedSessions();
      if (!bookmarkedSessions.includes(sessionId)) {
        return false;
      }
    }

    // Check all filter types dynamically
    return Object.entries(this.activeFilters).every(([filterType, activeFilters]) => {
      // Convert filterType to lowercase to match HTML data attributes
      const sessionValues = sessionItem.dataset[filterType.toLowerCase()];

      return SessionPlan.matchesFilter(sessionValues, activeFilters);
    });
  }

  static matchesFilter(sessionValues, activeFilters) {
    if (activeFilters.length === 0) return true;

    // If only 'all' is selected, show everything
    if (activeFilters.length === 1 && activeFilters.includes('all')) {
      return true;
    }

    // Check if session matches any selected value (excluding 'all')
    const specificValues = activeFilters.filter((value) => value !== 'all');
    if (specificValues.length === 0) return true;

    // Handle multiple session values (comma-separated)
    if (!sessionValues) return false;

    const sessionValueArray = sessionValues.split(',').filter((value) => value.trim() !== '');

    // Check if any of the session's categories match any of the selected filter values
    return sessionValueArray.some((sessionValue) => specificValues.includes(sessionValue));
  }

  hideEmptyGroups() {
    const sessionPlan = this.$el.querySelector('.js-session-plan');
    if (!sessionPlan) return;

    // Hide empty session subgroups
    const sessionSubgroups = sessionPlan.querySelectorAll('.session-subgroup');
    sessionSubgroups.forEach((subgroup) => {
      const visibleSessions = subgroup.querySelectorAll('.js-session-item:not(.filtered-out)');
      subgroup.style.display = visibleSessions.length === 0 ? 'none' : '';
    });

    // Hide empty session groups
    const sessionGroups = sessionPlan.querySelectorAll('.js-session-group');
    sessionGroups.forEach((group) => {
      const visibleSubgroups = group.querySelectorAll('.session-subgroup:not([style*="display: none"])');
      group.style.display = visibleSubgroups.length === 0 ? 'none' : '';
    });

    // Hide empty date groups
    const sessionDateGroups = sessionPlan.querySelectorAll('.js-session-date-group');
    sessionDateGroups.forEach((dateGroup) => {
      const visibleGroups = dateGroup.querySelectorAll('.js-session-group:not([style*="display: none"])');
      dateGroup.style.display = visibleGroups.length === 0 ? 'none' : '';
    });
  }

  initFilterMenuToggle() {
    const sessionFilter = this.$el.querySelector('.js-session-filter');
    const filterHeader = this.$el.querySelector('.js-session-filter-menu-toggle');
    filterHeader.addEventListener('click', (e) => {
      e.stopPropagation();
      sessionFilter.classList.toggle('open');
    });
  }

  clearFilters() {
    // Reset all filter types to 'all'
    Object.keys(this.activeFilters).forEach((filterType) => {
      this.activeFilters[filterType] = ['all'];
    });

    // Reset filter buttons
    const filterOptions = this.$el.querySelectorAll('.js-filter-option');
    filterOptions.forEach((option) => {
      const { filterValue } = option.dataset;
      if (filterValue === 'all') {
        option.classList.add('active');
      } else {
        option.classList.remove('active');
      }
    });

    // Show all sessions
    const sessionPlan = this.$el.querySelector('.js-session-plan');
    if (!sessionPlan) return;

    const sessionItems = sessionPlan.querySelectorAll('.js-session-item');
    sessionItems.forEach((item) => {
      item.classList.remove('filtered-out');
    });

    // Show all groups
    const allGroups = sessionPlan.querySelectorAll('.js-session-group, .js-session-date-group, .session-subgroup');
    allGroups.forEach((group) => {
      group.style.display = '';
    });
  }

  resetAllFilters() {
    // Reset category filters
    this.clearFilters();

    const bookmarkFilterBtn = this.$el.querySelector('.js-bookmark-filter');
    bookmarkFilterBtn.classList.add('inactive');
    bookmarkFilterBtn.classList.remove('active');

    // // Remove bookmark filter state
    this.$el.classList.remove('show-bookmarked-only');

    // Hide no results message (which also hides the reset button)
    const noResultsMessage = this.$el.querySelector('.js-no-results-message');
    noResultsMessage.style.display = 'none';
  }
}

SessionPlan.className = 'SessionPlan';
export default SessionPlan;
